/**
 * Entitlement Bypass — pure derivation for low-balance gate exemptions.
 *
 * Some users have a legitimate right to chat even when their OLV wallet
 * balance is $0:
 *
 *   - Trial users (`subscription.status === 'trialing'` with credit + a
 *     trial that has not yet expired). Prest0 is eating the cost.
 *   - Trial users on a legacy `plan === 'trial'` (and a future
 *     `trial_expires_at`), regardless of credit cents.
 *   - "Client free" users (`subscription.is_client_free === true`) —
 *     same idea: Prest0 has agreed to absorb cost.
 *   - Enterprise accounts (`accounts/{accountId}/billing/main.plan ===
 *     'enterprise'` with active subscription) — usage is paid for at
 *     the account level, not the wallet level.
 *   - Legacy `users/{uid}.trial.active === true` flag (still present
 *     on some early-adopter docs like Fernando Hidalgo's tenant).
 *
 * If ANY of these holds, the OLV-balance critical-threshold gate must
 * NOT block `handleSend`. This module exposes a single pure function so
 * that the React hook in `useEntitlementBypass.ts` can stay thin and so
 * that the bypass logic can be unit-tested without rendering React or
 * touching Firestore.
 *
 * Regression context (2026-05-13): Fernando Hidalgo's `enterprise` +
 * `trialing` tenant had no OLV wallet doc, so `walletSummary.summary
 * .olvBalance` defaulted to 0 → USD = 0 → `shouldBlock: true`. The
 * webchat refused to send. This bypass module reverses that for the
 * cases above.
 */
// ─────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────

/**
 * Anything that can sensibly be coerced into a millisecond epoch:
 * - Firestore Timestamp (`.toMillis()` or `{ seconds, nanoseconds }`)
 * - JS Date
 * - number (ms or seconds)
 * - ISO 8601 string ("2026-05-16", "2026-05-16T00:00:00Z", …)
 *
 * We accept all of these because `trial_expires_at` has been written in
 * multiple shapes over the lifetime of the schema (server Timestamp,
 * client-side ISO string, etc.).
 */
export type TrialExpiryValue =
  | { toMillis: () => number }
  | { seconds: number; nanoseconds?: number }
  | Date
  | number
  | string
  | null
  | undefined;

export interface SubscriptionDocLike {
  status?: string | null;
  plan?: string | null;
  is_client_free?: boolean | null;
  ai_enabled?: boolean | null;
  trial_credit_cents?: number | null;
  trial_expires_at?: TrialExpiryValue;
}

export interface AccountBillingDocLike {
  plan?: string | null;
  subscriptionStatus?: string | null;
  /** Some legacy docs use `status` instead of `subscriptionStatus`. */
  status?: string | null;
}

export interface UserDocLike {
  trial?: {
    active?: boolean | null;
  } | null;
}

export interface EntitlementBypassInput {
  subscription?: SubscriptionDocLike | null;
  accountBilling?: AccountBillingDocLike | null;
  userDoc?: UserDocLike | null;
  /** Optional override (used in tests). Defaults to `Date.now()`. */
  now?: Date | number;
}

/**
 * Detailed reason — primarily exposed for debugging / observability.
 * Consumers should usually only care about `hasBypass`.
 */
export type EntitlementBypassReason =
  | 'is_client_free'
  | 'subscription_trialing_with_credit'
  | 'plan_trial_not_expired'
  | 'account_enterprise_active'
  | 'legacy_trial_active';

export interface EntitlementBypassResult {
  hasBypass: boolean;
  reasons: EntitlementBypassReason[];
}

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

/**
 * Convert a TrialExpiryValue into a millisecond epoch, or `null` if it
 * can't be parsed. Tolerant of every shape we have observed in
 * Firestore for `trial_expires_at`.
 */
export function expiryToMillis(value: TrialExpiryValue): number | null {
  if (value === null || value === undefined) return null;

  // Firestore Timestamp (admin or client SDK both expose `.toMillis()`)
  if (typeof (value as { toMillis?: unknown }).toMillis === 'function') {
    try {
      const ms = (value as { toMillis: () => number }).toMillis();
      return Number.isFinite(ms) ? ms : null;
    } catch {
      return null;
    }
  }

  // Plain `{ seconds, nanoseconds }` (Firestore serialized form)
  if (
    typeof value === 'object' &&
    'seconds' in (value as object) &&
    typeof (value as { seconds: unknown }).seconds === 'number'
  ) {
    const seconds = (value as { seconds: number }).seconds;
    const nanos = (value as { nanoseconds?: number }).nanoseconds ?? 0;
    return seconds * 1000 + Math.floor(nanos / 1e6);
  }

  // JS Date
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }

  // Number — treat values < 1e12 as seconds (Unix epoch in seconds),
  // otherwise milliseconds. This matches the heuristic used elsewhere
  // in the codebase for Stripe timestamps.
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }

  // String — try ISO 8601 / date-only / RFC 2822 via Date.parse
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const ms = Date.parse(trimmed);
    return Number.isFinite(ms) ? ms : null;
  }

  return null;
}

function isFutureMs(ms: number | null, nowMs: number): boolean {
  if (ms === null) return false;
  return ms > nowMs;
}

// ─────────────────────────────────────────────────────────────────────
// Core
// ─────────────────────────────────────────────────────────────────────

/**
 * Returns `{ hasBypass, reasons }` for the given Firestore-derived
 * inputs. Pure — never reads Firestore, never reads the clock unless
 * `input.now` is omitted.
 */
export function deriveEntitlementBypass(
  input: EntitlementBypassInput
): EntitlementBypassResult {
  const reasons: EntitlementBypassReason[] = [];
  const nowMs =
    input.now === undefined
      ? Date.now()
      : input.now instanceof Date
      ? input.now.getTime()
      : input.now;

  const sub = input.subscription ?? null;
  const acct = input.accountBilling ?? null;
  const userDoc = input.userDoc ?? null;

  // 1) is_client_free flag — Prest0 absorbs cost.
  if (sub?.is_client_free === true) {
    reasons.push('is_client_free');
  }

  // 2) Active trial WITH remaining credit and a future expiry.
  if (
    sub?.status === 'trialing' &&
    typeof sub?.trial_credit_cents === 'number' &&
    sub.trial_credit_cents > 0 &&
    isFutureMs(expiryToMillis(sub?.trial_expires_at), nowMs)
  ) {
    reasons.push('subscription_trialing_with_credit');
  }

  // 3) Legacy `plan === 'trial'` with a future expiry — regardless of
  //    credit cents, this covers trial subscriptions whose credit field
  //    wasn't populated (early-adopter docs).
  if (
    sub?.plan === 'trial' &&
    isFutureMs(expiryToMillis(sub?.trial_expires_at), nowMs)
  ) {
    reasons.push('plan_trial_not_expired');
  }

  // 4) Enterprise account with active subscription — billing happens at
  //    the account level, not via OLV wallet.
  if (
    acct?.plan === 'enterprise' &&
    (acct.subscriptionStatus === 'active' || acct.status === 'active')
  ) {
    reasons.push('account_enterprise_active');
  }

  // 5) Legacy `users/{uid}.trial.active === true` (Fernando's doc).
  if (userDoc?.trial?.active === true) {
    reasons.push('legacy_trial_active');
  }

  return {
    hasBypass: reasons.length > 0,
    reasons,
  };
}
